import { useTRPC } from '@/utils/trpc';
import { ALLERGEN_VALUE_SET_URL, MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import { AddAllergyForm, type AddAllergyFormValues } from '@liza/ui/components/records/AddAllergyForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useValueSetExpand } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const CLINICAL_STATUS_VALUE_SET_URL = 'http://hl7.org/fhir/ValueSet/allergyintolerance-clinical';

type Props = {
  medplumPatientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddAllergyDialog = ({ medplumPatientId, isOpen, onOpenChange }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const allergenExpand = useValueSetExpand({
    queryOptions: (filter) => trpc.valueSet.expand.queryOptions({ url: ALLERGEN_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });

  // Surface RxNorm-coded members first: a drug allergen recorded with its RxNorm code lets the
  // e-prescriber run drug–allergy interaction checks, whereas the catalog's SNOMED members degrade to
  // a weaker name match. Food/environmental substances only have SNOMED codes and keep their order.
  // The backend resolver (resolveAllergen) applies the same RxNorm preference, so a chat-authored and
  // a picker-authored allergen for the same substance land on the same coding.
  // Depend on `.items` (the hook's memoized inner ref), not the hook result — that object is a fresh
  // literal every render, so depending on it would defeat the memo.
  const sortedItems = useMemo(
    () =>
      [...allergenExpand.items].sort(
        (a, b) =>
          Number(b.system === MEDICATION_CODE_SYSTEMS.RXNORM) - Number(a.system === MEDICATION_CODE_SYSTEMS.RXNORM),
      ),
    [allergenExpand.items],
  );
  const allergen = { ...allergenExpand, items: sortedItems };
  const clinicalStatus = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: CLINICAL_STATUS_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });

  const { mutateAsync: createAllergy, isPending: isCreating } = useMutation(
    trpc.patient.allergy.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.allergy.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: AddAllergyFormValues): Promise<void> => {
      try {
        await createAllergy({
          resource: {
            clinicalStatus: { coding: [values.clinicalStatus] },
            code: { coding: [values.allergen], text: values.allergen.display ?? values.allergen.code },
            onsetDateTime: values.onsetDate || undefined,
            patient: { reference: `Patient/${medplumPatientId}` },
            reaction: values.reaction ? [{ manifestation: [{ text: values.reaction }] }] : undefined,
            recordedDate: new Date().toISOString(),
            resourceType: 'AllergyIntolerance',
          },
        });
        toast.success(t('allergyAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addAllergyFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createAllergy, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addAllergy')}</DialogTitle>
        </DialogHeader>
        <AddAllergyForm
          allergen={allergen}
          clinicalStatus={clinicalStatus}
          isCreating={isCreating}
          onClose={() => onOpenChange(false)}
          onCreate={handleCreate}
        />
      </DialogContent>
    </Dialog>
  );
};
